/**
 * Erfassungsquelle „web-tab" (Issue #61): Web-Anwendung im eigenen Browser-Tab.
 *
 * Ersetzt die same-origin-Einbettung (WebIframeSource) als Standard: Die
 * Anwendung läuft in ihrem eigenen Tab unter ihrer eigenen Adresse, der
 * Tester unter seiner — keine gemeinsame Sicherheitszone mehr (kein geteilter
 * Speicher, keine Framing-Ausnahmen in der Anwendung, kein /tester/-Routing
 * im App-Vhost). Screenshots kommen über die Tab-Freigabe (FremdTabSource);
 * Stage/Live entsprechen der Stufenwahl des Web-Adapters (#37).
 *
 * Anmeldung: Tester und Anwendung hängen an derselben Authentik-Instanz —
 * der Melder ist per SSO in beiden derselbe Benutzer, ohne Session-Abgleich.
 */

import { openApp, type AppZiel } from "@/components/AppSwitcher"
import type { Application } from "@/types"
import { FremdTabSource, type FremdTabTexte } from "./FremdTabSource"

const TEXTE: FremdTabTexte = {
  intro:
    "Die Anwendung läuft in einem eigenen Browser-Tab. Öffne sie, arbeite dort wie gewohnt und komm zum Melden hierher zurück — der Tester greift den Tab per Freigabe ab.",
  tab: "Anwendungs-Tab",
  oeffnen: { live: "Live öffnen", dev: "Stage öffnen", einzig: "Anwendung öffnen" },
  zieleHinweis: (
    <>
      <span className="font-semibold">Live</span> = echte Daten,{" "}
      <span className="font-semibold">Stage</span> = Testumgebung mit dem neuesten Stand.
    </>
  ),
  schritt1: (
    <>
      <span className="font-semibold text-foreground">Anwendung öffnen</span> und dort zur
      Stelle navigieren, um die es geht.
    </>
  ),
  fehlerVerbinden: "Anwendungs-Tab konnte nicht verbunden werden",
  fehlerVerloren: "Verbindung zum Anwendungs-Tab verloren — bitte neu verbinden",
}

export interface WebTabSourceProps {
  anwendung: Application
  /** Aufgenommenes Bild — der Kern legt damit die Meldung an (#108) */
  onBild: (bild: Blob) => void
  onDateiWaehlen: () => void
  /** Kern arbeitet gerade (Meldung anlegen + Upload) */
  beschaeftigt?: boolean
  /** Umgebung der Meldung (Zielbild #36): dev = Stage, live = echte Daten */
  onUmgebungChange: (umgebung: AppZiel) => void
}

export function WebTabSource({ anwendung, onUmgebungChange, ...rest }: WebTabSourceProps) {
  const oeffnen = (ziel: AppZiel) => {
    const oeffnung = openApp(anwendung, ziel)
    if (oeffnung.modus === "tab") onUmgebungChange(oeffnung.umgebung)
  }
  return <FremdTabSource anwendung={anwendung} oeffnen={oeffnen} texte={TEXTE} {...rest} />
}
